"use client";

import { useActionState, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { extractApiErrorMessage } from "@/shared/api/error-handler";

import { useAuthStore } from "@/shared/model/auth-store";
import { api } from "@/shared/lib/api";
import { useAppNotifications } from "@/shared/lib/hooks/use-app-notifications-unified";
import { Button } from "@/shared/ui/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/modals/dialog";
import { Input } from "@/shared/ui/inputs/input";
import { Label } from "@/shared/ui/forms/label";
import { Switch } from "@/shared/ui/inputs/switch";

type Props = {
  tournamentId: string;
  defaultTeamName?: string;
  defaultLookingForPartner?: boolean;
  trigger: ReactNode;
  afterCreate?: (teamId: string) => void | Promise<void>;
  goToRegisterAfterCreate?: boolean;
};

export function TempTeamCreateDialog({
  tournamentId,
  defaultTeamName,
  defaultLookingForPartner,
  trigger,
  afterCreate,
  goToRegisterAfterCreate,
}: Props) {
  const tUi = useTranslations("Ui");
  const router = useRouter();
  const notifications = useAppNotifications();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [open, setOpen] = useState(false);
  const [teamName, setTeamName] = useState(defaultTeamName ?? "");
  // KAN-110: по умолчанию «ищем игрока» включён — иначе неполная temp-команда
  // невидима в списке открытых команд турнира (GetVisibleTournamentTeamsAsync
  // фильтрует по IsLookingForPartner) и её «теряют» сразу после создания.
  const [looking, setLooking] = useState(defaultLookingForPartner ?? true);
  const [, createAction, isSaving] = useActionState<
    { error: string | null },
    FormData
  >(async (_state) => {
    if (!isAuthenticated) {
      router.push("/login");
      return { error: "unauthorized" };
    }

    try {
      const res = await api.post(
        `/TournamentTeams/tournaments/${tournamentId}/temp-teams`,
        {
          name: teamName.trim() ? teamName.trim() : undefined,
          isLookingForPartner: looking,
        },
      );
      const id = res.data?.id as string | undefined;
      if (!id) {
        notifications.error(tUi("litTempTeamCreateDialog.neUdalosSozdatKomandu"));
        return { error: "missing-id" };
      }

      notifications.success(tUi("litTempTeamCreateDialog.komandaSozdana"));
      await afterCreate?.(id);

      if (goToRegisterAfterCreate) {
        setOpen(false);
        router.push(`/tournaments/${tournamentId}/register`);
        return { error: null };
      }

      setOpen(false);
    } catch (error: unknown) {
      const err = error as {
        code?: string;
        response?: { data?: { message?: string } };
      };
      if (err?.code === "ECONNABORTED") {
        notifications.error(tUi("litTempTeamCreateDialog.taymautZaprosaProverBackend"));
      } else {
        notifications.error(
          extractApiErrorMessage(err, tUi("litTempTeamCreateDialog.neUdalosSozdatKomandu")),
        );
      }
    }
    return { error: null };
  }, { error: null });

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!isAuthenticated) {
          router.push("/login");
          return;
        }
        setOpen(Boolean(v));
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- перехват всплывающего клика от trigger (кнопки); Enter/Space по ней дают тот же click */}
      <div
        onClick={(e) => {
          e.preventDefault();
          if (!isAuthenticated) {
            router.push("/login");
            return;
          }
          setOpen(true);
        }}
      >
        {trigger}
      </div>

      <DialogContent className="sm:max-w-[520px]">
        <form action={createAction}>
          <DialogHeader>
            <DialogTitle>{tUi("tempTeamCreateDialog.sozdanieVremennoyKomandy")}</DialogTitle>
            <DialogDescription>
              {tUi("tempTeamCreateDialog.dlya2v23v34v4")}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="temp-team-name">{tUi("tempTeamCreateDialog.nazvanieOptsionalno")}</Label>
              <Input
                id="temp-team-name"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder={tUi("tempTeamCreateDialog.naprimerDuo")}
              />
            </div>

            <div className="flex items-center justify-between gap-4 rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">{tUi("tempTeamCreateDialog.poiskTimmeyta")}</div>
                <div className="text-xs text-muted-foreground">
                  {tUi("tempTeamCreateDialog.esliVklyuchenoKomandaVidna")}
                </div>
              </div>
              <Switch checked={looking} onCheckedChange={setLooking} />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isSaving}
              type="button"
            >
              {tUi("tempTeamCreateDialog.otmena")}
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving
                ? tUi("litTempTeamCreateDialog.sozdanie")
                : goToRegisterAfterCreate
                  ? tUi("litTempTeamCreateDialog.sozdatIProdolzhit")
                  : tUi("litTempTeamCreateDialog.sozdat")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
